import * as d3 from 'd3';
import { useState, useMemo, useCallback } from 'react';
// Custom hooks
import useElementSize from './useElementSize';
import useTouchFocus from './useTouchFocus';
import useAnimatedNumber from './useAnimatedNumber';
// Tools
import { no_propagate } from './utils';
// Constants
import { ZOOM_ANIM_DURATION, UNFURL_ANIM_DURATION } from './constants';

// Same person colouring as the chord diagram, so a voyager keeps their identity
// across the two views.
const interpolateRainbowTruncated = (x) => d3.interpolateRainbow(1 - (1-x)*0.8);

const MARGIN_LEFT = 60;
const MARGIN_RIGHT = 150;
const MARGIN_TOP = 50;
const MARGIN_BOTTOM = 50;

// What everyone else drops to when one person is focused, and when one is
// merely selected.
const FOCUS_DIM_OPACITY = 0.33;
const PERSON_SELECT_DIM_OPACITY = 0.66;
const TRIP_SELECT_DIM_OPACITY = 0.5;

// How many of the most-travelled voyagers get a name at the end of their line,
// and how far apart those names sit as a multiple of their own font size.
const NUM_LABELS = 7;
const LABEL_GAP_RATIO = 1.3;

// Text is sized in user units, which the viewBox scales by (viewport width / 1000).
// On a phone that renders 15/17 user units at well under 10 CSS px, so hold the
// on-screen size to these floors instead. The multipliers below then keep the
// margins and label spacing in proportion to whatever size the text ends up.
const AXIS_FONT_SIZE = 12;
const LABEL_FONT_SIZE = 12;
const MIN_AXIS_FONT_PX = 10;
const MIN_LABEL_FONT_PX = 10;

// Linear: each increment ramps diagonally across the one trip that caused it,
// and the line runs flat between voyages.
const line_generator = d3.line();

// Full-screen graph of every voyager's running voyage count. x is the trips in
// chronological order, y is how many voyages that person has been on by then, so
// each series climbs by one across each trip its person went on.
const NoraMode = ({ people, trips, focused_person, selected_person, selected_trip,
                    set_focused_person, set_selected_person }) => {

  // Measure the container rather than the window, so the viewBox aspect ratio
  // always matches the box it is drawn into and nothing is cropped or
  // letterboxed. On mobile the two do not agree - see useElementSize.
  const [container_ref, { width: container_width, height: container_height }] = useElementSize();

  const [zoomed, set_zoomed] = useState(false);
  // Fix the width in user units and let height follow the container's aspect
  // ratio. Before the first measurement, fall back to square.
  const width = 1000;
  const height = container_width > 0 ? width * container_height / container_width : width;

  // One CSS pixel, in user units - the factor the viewBox is shrinking text by.
  const user_units_per_px = container_width > 0 ? width / container_width : 1;
  const axis_font_size = Math.max(AXIS_FONT_SIZE, MIN_AXIS_FONT_PX * user_units_per_px);
  const label_font_size = Math.max(LABEL_FONT_SIZE, MIN_LABEL_FONT_PX * user_units_per_px);

  // Gaps around the text, as multiples of it. The multipliers are chosen so that
  // at the unscaled font sizes these all come out at or under the MARGIN_*
  // minimums, leaving the desktop layout exactly as it was.
  const margin_left = Math.max(MARGIN_LEFT, 4 * axis_font_size);
  const margin_right = Math.max(MARGIN_RIGHT, 8 * label_font_size);
  const margin_bottom = Math.max(MARGIN_BOTTOM, 3 * axis_font_size);
  const tick_label_gap = 0.7 * axis_font_size;
  const y_axis_label_gap = 2.5 * axis_font_size;
  // Below the x axis, in order: the tick marks, the trip names they point at,
  // then the axis title clear of both.
  const x_tick_length = 0.5 * axis_font_size;
  const x_tick_label_gap = 1.4 * axis_font_size;
  const x_axis_label_gap = 2.6 * axis_font_size;
  const label_gap = Math.max(12, 0.7 * label_font_size);
  const label_min_gap = LABEL_GAP_RATIO * label_font_size;

  const plot_width = Math.max(width - margin_left - margin_right, 1);
  const plot_height = Math.max(height - MARGIN_TOP - margin_bottom, 1);

  // Same colour space as NodesLabels, so a voyager is the same colour in both views.
  const num_people = Object.keys(people).length;
  const colors = useMemo(() => d3.quantize(interpolateRainbowTruncated, num_people), [num_people]);

  // ***************************************************************************
  // * Series data
  // ***************************************************************************
  // Only filtered-in trips make it onto the x axis, and only filtered-in people
  // get a line, so the filter menu drives this view too.
  const plot_trips = useMemo(() => {
    return Object.values(trips)
                 .filter(trip => trip.in_filter)
  }, [trips]);

  const [series, max_count] = useMemo(() => {
    const trip_positions = new Map(plot_trips.map((trip, position) => [trip.trip_index, position]));
    let max_count = 0;
    const series = [];
    for (const person of Object.values(people)) {
      if (!person.in_filter) continue;
      const attended = new Set(person.person_trips.map(pt => pt.trip_index)
                                                  .filter(trip_index => trip_positions.has(trip_index)));
      // Start at zero to the left of the first trip, then carry the running
      // total, incrementing it on each attended trip.
      let count = 0;
      const points = [[-1, 0]];
      plot_trips.forEach((trip, position) => {
        if (attended.has(trip.trip_index)) count++;
        points.push([position, count]);
      });
      // Someone whose every voyage was filtered out has a flat zero line - drop it.
      if (count === 0) continue;
      max_count = Math.max(max_count, count);
      series.push({
        person_index: person.person_index,
        name: person.name,
        points,
        count
      });
    }
    return [series, max_count];
  }, [people, plot_trips]);

  // Positions in plot_trips of the selected person's own voyages, ascending.
  const selected_positions = useMemo(() => {
    if (selected_person === null) return [];
    const person = people[selected_person];
    const person_trips = new Set(person.person_trips.map(pt => pt.trip_index));
    return plot_trips.flatMap((trip, position) =>
      person_trips.has(trip.trip_index) ? [position] : []);
  }, [people, plot_trips, selected_person]);

  // ***************************************************************************
  // * Scales
  // ***************************************************************************
  // Zooming crops the left of the x axis to the selected person's first voyage.
  // It only changes the domain: counts still accumulate from the very first
  // trip, so everyone else's line enters at the left edge part way up rather
  // than restarting at zero. With nobody selected there is nothing to zoom to,
  // so it falls back to the full range.
  const zoom_start = (zoomed && selected_positions.length > 0) ? selected_positions[0] : 0;
  // One before the first trip in view is the zero baseline. Tweening this one
  // number animates the whole zoom: every mark, tick and gridline is placed
  // through x(), so they all slide and spread together.
  const x_domain_start = useAnimatedNumber(zoom_start - 1, ZOOM_ANIM_DURATION);
  // On mount the end of the axis grows out to the last trip, unfurling the graph
  // left to right. The visible domain is stretched to fill the whole graph area.
  // unfurl_progress is a tween from 0 to 1 and we use this to determine
  // x_domain_end. We do it this way instead of directly setting
  //   x_domain_end = useAnimatedNumber(plot_trips.length, ...)
  // since that would retrigger the animation every time plot_trips.length changes
  // (e.g. when changing the filter). The unfurl_progress animation runs once
  // when NoraMode is mounted and never again.
  // Skipping drops the duration to zero, which useAnimatedNumber treats as
  // "go straight to the target": the in-flight tween is cancelled and the graph
  // lands on its finished state on the next render.
  const [unfurl_skipped, set_unfurl_skipped] = useState(false);
  const unfurl_progress = useAnimatedNumber(1, unfurl_skipped ? 0 : UNFURL_ANIM_DURATION, 0);
  const x_domain_end = unfurl_progress * Math.max(plot_trips.length - 1, 0);
  const x_domain_span = Math.max(x_domain_end - x_domain_start, 1);

  // Everyone's running total at a (possibly fractional) point on the x axis,
  // interpolated exactly as the lines are drawn between trips.
  const count_at = (points, position) => {
    const index = Math.max(0, Math.min(position + 1, points.length - 1));
    const low = Math.floor(index);
    const high = Math.min(low + 1, points.length - 1);
    return points[low][1] + (points[high][1] - points[low][1]) * (index - low);
  };

  // The y axis is scaled to the tallest line so far rather than the final
  // tallest, so it grows as the graph unfurls instead of leaving the early
  // trips squashed along the bottom.
  const visible_max_count = useMemo(() => {
    return series.reduce((max, s) => Math.max(max, count_at(s.points, x_domain_end)), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, x_domain_end]);

  const x = (position) => margin_left + plot_width * (position - x_domain_start) / x_domain_span;
  const y = (count) => MARGIN_TOP + plot_height * (1 - count / Math.max(visible_max_count, 1));

  // Lines are drawn in full and cropped by the plot's clip path: anything past
  // the end of the domain lands beyond the right edge, so no reveal is needed.
  const paths = useMemo(() => {
    return series.map(s => ({
      ...s,
      d: line_generator(s.points.map(([position, count]) => [x(position), y(count)]))
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, plot_width, plot_height, visible_max_count, x_domain_start, x_domain_span]);

  // Integer ticks, thinned out so there are never more than about ten.
  const y_axis_ticks = useMemo(() => {
    const top = Math.floor(visible_max_count);
    const step = Math.max(1, Math.ceil(top / 10));
    return d3.range(0, top + 1, step);
  }, [visible_max_count]);

  // The x axis gets two ticks rather than a scale, marking the span of the
  // selected person's voyages: their first and their most recent. With nobody
  // selected there is no span to mark, so there are no ticks.
  const x_axis_ticks = useMemo(() => {
    if (selected_positions.length === 0) return [];
    // Already ascending, so these are their earliest and latest. A Set in case
    // they have only been on the one voyage.
    const positions = new Set([selected_positions[0], selected_positions[selected_positions.length - 1]]);
    return [...positions].map(position => ({
      position,
      label: plot_trips[position].trip_key_memory
    }));
  }, [plot_trips, selected_positions]);

  // ***************************************************************************
  // * Emphasis
  // ***************************************************************************
  const selected_trip_people = useMemo(() => {
    if (selected_trip === null) return [];
    return trips[selected_trip].person_trips.map((pt) => pt.person_index);
  }, [trips, selected_trip])
  // Focusing someone (hovering their line) pushes everyone else well back, so
  // the one line reads on its own. Selecting a trip or person is a lighter touch:
  // their line(s) is/are thickened and the rest only come down enough to sit
  // behind it. Focus wins while both are set.
  const opacity_for = (person_index) => {
    if (focused_person !== null) {
      return person_index === focused_person ? 1 : FOCUS_DIM_OPACITY;
    }
    if (selected_trip !== null) {
      return selected_trip_people.includes(person_index) ? 1 : TRIP_SELECT_DIM_OPACITY;
    }
    if (selected_person !== null) {
      // During the initial unfurl, start with all lines at full opacity and
      // ease them down to the dimmed value as the graph grows out.
      const opacity_of_nonselected = 1 + (PERSON_SELECT_DIM_OPACITY - 1) * unfurl_progress;
      return person_index === selected_person ? 1 : opacity_of_nonselected;
    }
    return 1;
  };

  // Determine which lines get drawn later and therefore appear on top.
  // Focused person takes highest priority, then a selected person, then
  // anyone on the selected trip.
  const z_pos_of_line = useCallback((person_index) => {
    if (person_index === focused_person)             return 3;
    if (person_index === selected_person)            return 2;
    if (selected_trip_people.includes(person_index)) return 1;
    return 0;
  }, [focused_person, selected_person, selected_trip_people]);

  const draw_order = useMemo(() => {
    const rank = (s) => z_pos_of_line(s.person_index);
    return [...paths].sort((a, b) => rank(a) - rank(b));
  }, [paths, z_pos_of_line]);

  // ***************************************************************************
  // * Right-hand labels
  // ***************************************************************************
  // The most-travelled few get a name at the end of their line, plus anyone
  // focused or selected. Lines that finish on the same count would otherwise
  // land their labels on top of each other, so nudge them apart and draw a
  // leader back to the line end.
  const label_x = margin_left + plot_width + label_gap;
  const labels = useMemo(() => {
    const chosen = [...paths].sort((a, b) => b.count - a.count).slice(0, NUM_LABELS);
    // Add lines for focused and selected people if they aren't already included.
    for (const person_index of [focused_person, selected_person]) {
      if (person_index === null || chosen.some(s => s.person_index === person_index)) continue;
      const extra = paths.find(s => s.person_index === person_index);
      if (extra) chosen.push(extra);
    }

    // Build a placement record per label, ordered top to bottom as the two
    // passes below require. Each carries the running total to print, line_y
    // (where that person's line currently ends) and label_y (where the text
    // goes). line_y values stay fixed but label_y values are nudged in order
    // to prevent overlaps.
    // If the running count is zero, don't show the label (i.e. labels only
    // appear during the unfurl animation once the person has been on at least
    // one trip.)
    const placed = chosen.map(s => {
      const running_count = count_at(s.points, x_domain_end);
      return { ...s, running_count: Math.round(running_count),
               line_y: y(running_count), label_y: y(running_count) };
    }).filter(s => s.running_count > 0).sort((a, b) => a.line_y - b.line_y);
    if (placed.length === 0) {
      return placed;
    }
    // Push down through the stack to open up the minimum gap...
    for (let i = 1; i < placed.length; i++) {
      placed[i].label_y = Math.max(placed[i].label_y, placed[i-1].label_y + label_min_gap);
    }
    // ...then back up, in case that pushed the bottom labels off the plot.
    const plot_bottom = MARGIN_TOP + plot_height;
    placed[placed.length - 1].label_y = Math.min(placed[placed.length - 1].label_y, plot_bottom);
    for (let i = placed.length - 1; i > 0; i--) {
      placed[i-1].label_y = Math.min(placed[i-1].label_y, placed[i].label_y - label_min_gap);
    }
    return placed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths, focused_person, selected_person, plot_height, visible_max_count, x_domain_end, label_min_gap]);


  // The axes are not clipped, so a tick for a trip the unfurl has not reached
  // yet would be drawn out beyond the right edge of the plot.
  const visible_x_axis_ticks = x_axis_ticks.filter(tick => tick.position <= x_domain_end);

  // Every tick label is centred on its tick, then nudged if that would put it
  // off the canvas or into the label to its right.
  const x_tick_label_x = (tick, index) => {
    // Rough enough to keep the two x-axis labels off each other without measuring
    // the rendered text.
    const estimate_text_width = (text, font_size) => text.length * 0.52 * font_size;

    const half_width = estimate_text_width(tick.label, axis_font_size) / 2;
    let centre = Math.min(Math.max(x(tick.position), half_width), width - half_width);
    if (index < visible_x_axis_ticks.length - 1) {
      const next = visible_x_axis_ticks[index + 1];
      const next_left = x(next.position) - estimate_text_width(next.label, axis_font_size) / 2;
      centre = Math.min(centre, next_left - axis_font_size - half_width);
    }
    return centre;
  };

  // In Nora mode, when we set a selected person, return focused person
  // to null (otherwise all other lines would stay at opacity FOCUS_DIM_OPACITY
  // but we want them to go back to PERSON_SELECT_DIM_OPACITY).
  const nora_mode_set_selected_person = useCallback((person_index) => {
    set_selected_person(person_index);
    set_focused_person(null);
  }, [set_selected_person, set_focused_person]);

  // There are no trips to focus here, so only the person handlers go in.
  const { on_touch_move, on_touch_end } = useTouchFocus({
    focused_person,
    set_focused_person,
    set_selected_person : nora_mode_set_selected_person
  });

  // The two share a slot in the corner of the plot: skip while the graph is
  // unfurling, then zoom once it has finished and there is a person to zoom to.
  const show_skip_button = unfurl_progress < 1;
  const show_zoom_button = (selected_positions.length > 0) && (unfurl_progress === 1);

  // The buttons are HTML elements overlaying the svg rather than part of it, so
  // that they can wear .header_button and stay one physical size. That means their
  // position has to be converted out of user units into the px the svg is
  // scaled to, to line its top left up with the top of the plot and the y axis.
  const px_per_user_unit = container_width > 0 ? container_width / width : 0;
  const plot_button_style = { top: MARGIN_TOP * px_per_user_unit + 10,
                              left: margin_left * px_per_user_unit + 10 };

  return (
    <div id="nora_container" ref={container_ref}>
      {show_skip_button &&
        <button className="header_button nora_plot_button"
                style={plot_button_style}
                onClick={no_propagate(() => set_unfurl_skipped(true))}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <polygon points="7 6 15 12 7 18" />
            <line x1="18" y1="6" x2="18" y2="18" />
          </svg>
        </button>}
      {show_zoom_button &&
        <button className="header_button nora_plot_button"
                style={plot_button_style}
                onClick={no_propagate(() => set_zoomed(!zoomed))}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
            {!zoomed && <line x1="11" y1="8" x2="11" y2="14" />}
          </svg>
        </button>}
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet"
           onTouchMove={on_touch_move}
           onTouchEnd={on_touch_end}>
        <g id="nora_axes">
          {y_axis_ticks.map(tick => (
            <g key={tick}>
              <line className="nora_gridline" x1={margin_left} x2={margin_left + plot_width} y1={y(tick)} y2={y(tick)}/>
              <text className="nora_tick_label" x={margin_left - tick_label_gap} y={y(tick)} dy="0.32em"
                    fontSize={axis_font_size}>{tick}</text>
            </g>
          ))}
          <line className="nora_axis" x1={margin_left} x2={margin_left} y1={MARGIN_TOP} y2={MARGIN_TOP + plot_height}/>
          <text className="nora_axis_label" fontSize={axis_font_size}
                transform={`translate(${margin_left - y_axis_label_gap}, ${MARGIN_TOP + plot_height/2}) rotate(-90)`}>
            Voyage count
          </text>
          {visible_x_axis_ticks.map((tick, index) => (
            <g key={tick.position}>
              <line className="nora_axis"
                    x1={x(tick.position)} x2={x(tick.position)}
                    y1={MARGIN_TOP + plot_height} y2={MARGIN_TOP + plot_height + x_tick_length}/>
              <text className="nora_x_tick_label" fontSize={axis_font_size}
                    x={x_tick_label_x(tick, index)} y={MARGIN_TOP + plot_height + x_tick_label_gap}>
                {tick.label}
              </text>
            </g>
          ))}
          {(selected_person === null) && (
            <text className="nora_axis_label" fontSize={axis_font_size}
                  x={margin_left + plot_width/2} y={MARGIN_TOP + plot_height + x_axis_label_gap}>
              Voyages
            </text>
          )}
        </g>

        {/* When zoomed, the part of each line before the zoom start would run
            out into the left margin, so keep the marks inside the plot. */}
        <defs>
          <clipPath id="nora_plot_clip">
            <rect x={margin_left} y={MARGIN_TOP} width={plot_width} height={plot_height}/>
          </clipPath>
        </defs>

        <g id="nora_lines" className={unfurl_progress < 1 ? "nora_unfurling" : ""}
           clipPath="url(#nora_plot_clip)">
          {draw_order.map(s => (
            <path key={s.person_index}
                  className={"nora_line" + (s.person_index === selected_person ? " nora_line_selected" : "")}
                  d={s.d}
                  opacity={opacity_for(s.person_index)}
                  stroke={colors[s.person_index]}/>
          ))}
        </g>

        {/* Fat transparent copies of each line, purely to give the 2px strokes a
            usable hit target. */}
        <g id="nora_hit_areas" clipPath="url(#nora_plot_clip)">
          {paths.map(s => (
            <path key={s.person_index}
                  className="nora_hit_area"
                  data-person_index={s.person_index}
                  d={s.d}
                  onMouseEnter={() => set_focused_person(s.person_index)}
                  onMouseLeave={() => set_focused_person(null)}
                  onClick={no_propagate(() => nora_mode_set_selected_person(s.person_index))}/>
          ))}
        </g>

        <g id="nora_labels">
          {labels.map(s => {
            return (
              <g key={s.person_index} opacity={opacity_for(s.person_index)}>
                {Math.abs(s.label_y - s.line_y) > 1 &&
                  <polyline className="nora_leader"
                            points={`${margin_left + plot_width},${s.line_y} ${label_x - label_gap/2},${s.label_y} ${label_x - 2},${s.label_y}`}
                            stroke={colors[s.person_index]}/>}
                <text className={"nora_line_label" + (s.person_index === selected_person ? " selected" : "")}
                      data-person_index={s.person_index}
                      x={label_x} y={s.label_y} dy="0.32em"
                      fontSize={label_font_size}
                      fill={colors[s.person_index]}
                      onMouseEnter={() => set_focused_person(s.person_index)}
                      onMouseLeave={() => set_focused_person(null)}
                      onClick={no_propagate(() => nora_mode_set_selected_person(s.person_index))}>
                  {s.name} ({s.running_count})
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default NoraMode;
